import { getLeagueSpotlightWeight } from './leagueUtils';
import { getTeamSpotlightWeight } from './teamUtils';
import { getMatchPhase } from './matchCountdown';

const isSpotlightEligible = (match) =>
  getMatchPhase(match?.matchDate, match?.result) !== 'finished';

/**
 * Composite score for "today's top match" toast: league tier + both clubs spotlight weights.
 * @param {object} match - prediction-like object with league, homeTeam, awayTeam
 * @returns {number}
 */
export const scoreMatchForSpotlight = (match) => {
  if (!match || typeof match !== 'object') return Number.NEGATIVE_INFINITY;
  return (
    getLeagueSpotlightWeight(match.league) +
    getTeamSpotlightWeight(match.homeTeam) +
    getTeamSpotlightWeight(match.awayTeam)
  );
};

/**
 * @param {object} a
 * @param {object} b
 * @returns {number} negative if a wins tie-break, positive if b wins, 0 if equal
 */
const compareSpotlightCandidates = (a, b) => {
  const sa = scoreMatchForSpotlight(a);
  const sb = scoreMatchForSpotlight(b);
  if (sa !== sb) return sb - sa;
  const va = a.views ?? 0;
  const vb = b.views ?? 0;
  if (va !== vb) return vb - va;
  const la = a.likes ?? 0;
  const lb = b.likes ?? 0;
  if (la !== lb) return lb - la;
  return String(a.slug || '').localeCompare(String(b.slug || ''));
};

/**
 * Pick best match for spotlight toast. Tie-break: score, then views, likes, slug.
 * When all spotlight scores are 0, ordering falls through to views/likes/slug.
 *
 * @param {object[]} matches - non-empty array of predictions for the same calendar day
 * @returns {object | null}
 */
export const pickTopMatch = (matches) => {
  if (!Array.isArray(matches) || matches.length === 0) return null;
  const eligible = matches.filter(isSpotlightEligible);
  if (eligible.length === 0) return null;
  return eligible.reduce((best, m) => (compareSpotlightCandidates(best, m) > 0 ? m : best));
};
